/**
 * Off-Label Use of Human Drugs consent form — PDF component and field definitions.
 *
 * Auto-filled: clinic header, owner details, patient details.
 * Manual fields: treatment/drug name.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { OwnerDetails } from '@/frontend/features/printing/documents/blocks/owner-details'
import { OwnerSignature } from '@/frontend/features/printing/documents/blocks/owner-signature'
import { PatientDetails } from '@/frontend/features/printing/documents/blocks/patient-details'
import {
  FillField,
  WithInlineResponse,
} from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'

export interface OffLicenceConsentFields {
  treatmentName: string
}

export const defaultOffLicenceConsentFields: OffLicenceConsentFields = {
  treatmentName: '',
}

export const OFF_LICENCE_CONSENT_TEXT =
  'I, the undersigned pet owner or authorised representative of the pet named above, hereby give my informed consent for the off-label use of human drugs in the treatment of my pet. I understand that the veterinarian or authorised staff at Snoots Vet may recommend this off-label use of a medication because:'

export const OFF_LICENCE_REASONS = [
  "The pet's condition does not have an approved veterinary drug available.",
  "The veterinarian believes that the human drug may be effective in treating the pet's condition.",
]

export const OFF_LICENCE_RISKS_INTRO =
  'I have been informed of the potential risks, benefits, and uncertainties associated with the off-label use of this medication.'

export const OFF_LICENCE_ACKNOWLEDGEMENTS_INTRO =
  'I understand that the off-label use of a human drug in my pet is not guaranteed to be safe or effective. I acknowledge the following:'

export const OFF_LICENCE_ACKNOWLEDGEMENTS = [
  'There may be limited scientific research and data available regarding the use of this medication in pets.',
  'The medication may have side effects or adverse reactions that are not fully understood in pets.',
  'The dosage and administration of the medication may differ from that of human usage.',
]

export const OFF_LICENCE_DISCUSSION_TEXT =
  'I have had the opportunity to discuss this treatment option with the veterinarian, and my questions and concerns have been addressed to my satisfaction. I understand that I can request more information at any time.'

export const OFF_LICENCE_DECLINE_TEXT =
  'I also understand that I have the right to decline this off-label treatment and request alternative treatments, if available. I am aware that the decision to use a human drug off-label in my pet is ultimately my choice.'

export const OFF_LICENCE_SIGNING_TEXT =
  'By signing below, I acknowledge that I have read, understood, and agree to the terms outlined in this consent form. I consent to the off-label use of human drugs in the treatment of my pet at Snoots.'

const styles = StyleSheet.create({
  ...baseStyles,
})

export function OffLicenceConsentPdf({
  context,
  fields,
}: {
  context: DocumentContext
  fields: OffLicenceConsentFields
}) {
  const { patient, owner, clinic } = context

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        <Text style={styles.title}>CONSENT FORM FOR OFF-LABEL USE OF HUMAN DRUGS IN PETS</Text>

        <OwnerDetails owner={owner} />

        <View style={styles.sectionGap}>
          <PatientDetails patient={patient} weight={context.weight} showDob showMicrochip />
        </View>

        <View style={styles.sectionGap}>
          <WithInlineResponse response={<FillField value={fields.treatmentName || undefined} />}>
            <Text style={styles.bold}>Consent for treatment with</Text>
          </WithInlineResponse>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>{OFF_LICENCE_CONSENT_TEXT}</Text>
          {OFF_LICENCE_REASONS.map((reason) => (
            <Text key={reason.slice(0, 30)} style={styles.listItem}>
              • {reason}
            </Text>
          ))}
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>{OFF_LICENCE_RISKS_INTRO}</Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>{OFF_LICENCE_ACKNOWLEDGEMENTS_INTRO}</Text>
          {OFF_LICENCE_ACKNOWLEDGEMENTS.map((ack) => (
            <Text key={ack.slice(0, 30)} style={styles.listItem}>
              • {ack}
            </Text>
          ))}
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>{OFF_LICENCE_DISCUSSION_TEXT}</Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>{OFF_LICENCE_DECLINE_TEXT}</Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={{ ...styles.text, ...styles.bold }}>{OFF_LICENCE_SIGNING_TEXT}</Text>
        </View>

        <OwnerSignature owner={owner} />
      </Page>
    </Document>
  )
}
